import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { Close, Download, Pause, PlayArrow } from '@mui/icons-material';
import api from '../../services/api';
import { isVoiceMedia } from './mediaFile';
import { wa } from './waTheme';

function isProxyPath(src) {
  return typeof src === 'string' && src.startsWith('/api/whatsapp/media/');
}

function kindOf(media, messageType) {
  const hinted = String(messageType || '').toLowerCase();
  if (hinted === 'image' || hinted === 'video' || hinted === 'audio') return hinted;
  const mime = String(media?.mime_type || '').toLowerCase();
  const name = String(media?.filename || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp)$/.test(name)) return 'image';
  if (mime.startsWith('video/') || /\.(mp4|mov|webm|3gp)$/.test(name)) return 'video';
  if (mime.startsWith('audio/') || /\.(ogg|opus|mp3|m4a|aac|wav|amr)$/.test(name)) return 'audio';
  return 'document';
}

function docBadge(filename) {
  const ext = String(filename || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return { label: 'PDF', color: '#e53935' };
  if (ext === 'doc' || ext === 'docx') return { label: 'DOC', color: '#1a73e8' };
  if (ext === 'xls' || ext === 'xlsx') return { label: 'XLS', color: '#1b8a3a' };
  if (ext === 'ppt' || ext === 'pptx') return { label: 'PPT', color: '#e67e22' };
  if (ext === 'txt') return { label: 'TXT', color: '#667781' };
  return { label: 'FILE', color: '#54656f' };
}

function waveBars(seed, count = 26) {
  let hash = 0;
  const value = String(seed || 'voice');
  for (let i = 0; i < value.length; i += 1) hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  const bars = [];
  for (let i = 0; i < count; i += 1) {
    hash = (hash * 1664525 + 1013904223) >>> 0;
    bars.push(6 + (hash % 16));
  }
  return bars;
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function AudioBubble({ src, media }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const voice = isVoiceMedia(media);
  const bars = useMemo(() => waveBars(media?.id || media?.filename), [media?.id, media?.filename]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 230, py: 0.5, px: 0.5 }}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => {
          const audio = e.currentTarget;
          if (audio.duration) setProgress(audio.currentTime / audio.duration);
        }}
      />
      <IconButton
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        sx={{ bgcolor: wa.send, color: '#fff', width: 38, height: 38, '&:hover': { bgcolor: '#008f72' } }}
      >
        {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 26 }}>
          {bars.map((height, index) => {
            const played = index / bars.length <= progress;
            return (
              <Box
                key={index}
                sx={{
                  width: 3,
                  height,
                  borderRadius: 1,
                  bgcolor: played ? wa.send : '#c5ccd1',
                }}
              />
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 11, color: wa.meta, mt: 0.25 }}>
          {voice ? 'Voice message' : (media.filename || 'Audio')}
          {' · '}
          {formatClock(playing || progress ? progress * duration : duration)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function WhatsAppMedia({ media, alt, messageType, onExpired }) {
  const [blobUrl, setBlobUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;
  const src = media?.url;
  const mimeType = media?.mime_type;
  const kind = kindOf(media, messageType);

  // Reload only when this file's address or type changes. A thread poll must
  // not tear down a video that is already on screen.
  useEffect(() => {
    if (!src || !isProxyPath(src)) return undefined;
    let objectUrl = '';
    let cancelled = false;
    setBlobUrl('');
    setFailed(false);

    const showBlob = (blob) => {
      if (cancelled) return;
      if (blob?.type && String(blob.type).includes('application/json')) {
        setFailed(true);
        return;
      }
      const headerType = blob.type && !/json|octet-stream/i.test(blob.type)
        ? blob.type
        : (mimeType || blob.type || '');
      const fileBlob = headerType ? new Blob([blob], { type: headerType }) : blob;
      const nextUrl = URL.createObjectURL(fileBlob);
      if (cancelled) {
        URL.revokeObjectURL(nextUrl);
        return;
      }
      objectUrl = nextUrl;
      setBlobUrl(nextUrl);
    };

    (async () => {
      try {
        let res;
        try {
          res = await api.get(src, { responseType: 'blob', timeout: 60000 });
        } catch (err) {
          if (cancelled) return;
          if (err?.response?.status !== 403) throw err;
          onExpiredRef.current?.();
          if (cancelled) return;
          res = await api.get(src, { responseType: 'blob', timeout: 60000 });
        }
        if (!cancelled) showBlob(res.data);
      } catch (err) {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, mimeType]);

  if (!media) return null;
  const filename = media.filename || (kind === 'image' ? 'Photo' : kind === 'video' ? 'Video' : 'File');

  if (!isProxyPath(src) || failed) {
    return (
      <Typography sx={{ fontSize: 13, color: wa.meta, px: 0.5, py: 0.5 }}>
        {failed ? `Couldn't load ${filename}` : filename}
      </Typography>
    );
  }

  if (!blobUrl) {
    return <Box sx={{ width: kind === 'document' ? 220 : 240, height: kind === 'audio' ? 54 : 140, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1 }} />;
  }

  if (kind === 'image') {
    return (
      <>
        <Box
          component="img"
          src={blobUrl}
          alt={alt || filename}
          onClick={() => setOpen(true)}
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: 330,
            maxHeight: 360,
            objectFit: 'cover',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        />
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
          <Box sx={{ position: 'relative', bgcolor: '#111b21' }}>
            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Close"
              sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}
            >
              <Close />
            </IconButton>
            <Box component="img" src={blobUrl} alt={alt || filename} sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }} />
          </Box>
        </Dialog>
      </>
    );
  }

  if (kind === 'video') {
    return (
      <Box
        component="video"
        src={blobUrl}
        controls
        playsInline
        sx={{ display: 'block', width: '100%', maxWidth: 330, maxHeight: 360, borderRadius: '6px', bgcolor: '#000' }}
      />
    );
  }

  if (kind === 'audio') return <AudioBubble src={blobUrl} media={media} />;

  const badge = docBadge(filename);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 230,
        maxWidth: 320,
        bgcolor: 'rgba(0,0,0,0.05)',
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 42,
          borderRadius: 0.6,
          bgcolor: badge.color,
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontSize: 10,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {badge.label}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 14, color: wa.text }}>{filename}</Typography>
        <Typography sx={{ fontSize: 12, color: wa.meta }}>{badge.label}</Typography>
      </Box>
      <IconButton
        component="a"
        href={blobUrl}
        download={filename}
        aria-label={`Download ${filename}`}
        size="small"
        sx={{ color: wa.icon }}
      >
        <Download fontSize="small" />
      </IconButton>
    </Box>
  );
}
